import React from 'react';
import { Dismiss20Regular, Info16Regular, Open16Regular } from '@fluentui/react-icons';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { PatchProjectData } from '../../../redux/projectDataSlice';
import styles from '../css/vdr.module.css';

const getVdrLinkParts = url => {
    if (!url) {
        return { href: '', title: '', displayUrl: '' };
    }
    try {
        const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
        const parsed = new URL(href);
        const title = parsed.hostname.replace(/^www\./i, '');
        return { href, title, displayUrl: href };
    } catch {
        return { href: url, title: url, displayUrl: url };
    }
};

const formatExternalVdrTimestamp = value => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return format(date, 'dd MMM yyyy · HH:mm');
};

const getUserDisplayName = user => user?.full_name?.trim() || user?.email?.trim() || null;

const ExternalVDRModal = ({ open, onClose }) => {
    const dispatch = useDispatch();
    const { selectedProject } = useSelector(state => state?.projectData);
    const { user } = useSelector(state => state?.user);
    const externalVdr = selectedProject?.external_vdr;
    const hasExistingVDR = Boolean(externalVdr?.vdr_link);
    const [link, setLink] = React.useState(externalVdr?.vdr_link || '');
    const [notes, setNotes] = React.useState(externalVdr?.vdr_description || '');
    const [isEditing, setIsEditing] = React.useState(!hasExistingVDR);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        if (!open) return;
        setLink(externalVdr?.vdr_link || '');
        setNotes(externalVdr?.vdr_description || '');
        setIsEditing(!externalVdr?.vdr_link);
    }, [open, externalVdr?.vdr_link, externalVdr?.vdr_description]);

    if (!open) return null;

    const patchExternalVdr = async (payload, successMessage) => {
        await dispatch(
            PatchProjectData({
                data: { external_vdr: payload },
                id: selectedProject?.id,
                suppressSuccessToast: true,
            }),
        ).unwrap();
        toast.success(successMessage);
    };

    const handleClose = () => {
        setIsEditing(!hasExistingVDR);
        onClose();
    };

    const handleSave = async () => {
        if (!link.trim()) {
            toast.error('Please enter a VDR link');
            return;
        }
        setLoading(true);
        try {
            const savedByName = getUserDisplayName(user) || externalVdr?.saved_by_name;
            await patchExternalVdr(
                {
                    ...externalVdr,
                    vdr_link: link.trim(),
                    vdr_description: notes,
                    saved_by_name: savedByName,
                    last_updated: new Date().toISOString(),
                },
                hasExistingVDR ? 'External VDR link updated' : 'External VDR link saved',
            );
            setIsEditing(false);
            onClose();
        } catch {
            toast.error('Failed to save external VDR');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        setLoading(true);
        try {
            await patchExternalVdr({ vdr_link: null, vdr_description: null }, 'External VDR removed');
            onClose();
        } catch {
            toast.error('Failed to remove external VDR');
        } finally {
            setLoading(false);
        }
    };

    const handleCopyLink = async () => {
        const { href } = getVdrLinkParts(externalVdr?.vdr_link);
        if (!href) return;
        try {
            await navigator.clipboard.writeText(href);
            toast.success('Link copied');
        } catch {
            toast.info(href);
        }
    };

    const { href, title, displayUrl } = getVdrLinkParts(externalVdr?.vdr_link);
    const savedBy =
        externalVdr?.saved_by_name ||
        externalVdr?.updated_by_name ||
        externalVdr?.saved_by ||
        externalVdr?.updated_by ||
        null;
    const lastUpdated = formatExternalVdrTimestamp(
        externalVdr?.last_updated || externalVdr?.updated_at || selectedProject?.project_modified,
    );
    const showView = hasExistingVDR && !isEditing;

    return (
        <div className={styles.overlay} role="dialog" aria-modal="true">
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>External VDR</h2>
                    <button type="button" className={styles.iconBtn} onClick={handleClose} aria-label="Close">
                        <Dismiss20Regular />
                    </button>
                </div>

                {showView ? (
                    <>
                        <div className={styles.modalBody}>
                            <p className={styles.externalVdrEyebrow}>External VDR</p>
                            <h3 className={styles.externalVdrTitle}>{title}</h3>
                            <p className={styles.externalVdrUrl}>{displayUrl}</p>
                            <div className={styles.externalVdrActions}>
                                <a
                                    className={styles.primaryBtn}
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer">
                                    Open external VDR
                                    <Open16Regular />
                                </a>
                                <button type="button" className={styles.secondaryBtn} onClick={handleCopyLink}>
                                    Copy link
                                </button>
                            </div>

                            <div className={styles.externalVdrNotesBlock}>
                                <p className={styles.externalVdrEyebrow}>Notes / access instructions</p>
                                <p className={styles.externalVdrNotesText}>
                                    {externalVdr?.vdr_description?.trim() ? externalVdr.vdr_description : '—'}
                                </p>
                            </div>

                            <div className={styles.externalVdrInfoBanner}>
                                <Info16Regular className={styles.externalVdrInfoIcon} aria-hidden />
                                <div>
                                    <p className={styles.externalVdrInfoLead}>
                                        Lens cannot analyse files held only in this external VDR.
                                    </p>
                                    <p className={styles.externalVdrInfoSub}>
                                        Upload required documents to Lens if they should be available to Ask Lens, IRL analysis,
                                        Observations or Intelligence Insights.
                                    </p>
                                </div>
                            </div>

                            <div className={styles.externalVdrMetaRow}>
                                <div className={styles.externalVdrMetaItem}>
                                    <span className={styles.externalVdrMetaLabel}>Saved by</span>
                                    <span className={styles.externalVdrMetaValue}>{savedBy || '—'}</span>
                                </div>
                                <div className={styles.externalVdrMetaItem}>
                                    <span className={styles.externalVdrMetaLabel}>Last updated</span>
                                    <span className={styles.externalVdrMetaValue}>{lastUpdated || '—'}</span>
                                </div>
                            </div>
                        </div>
                        <div className={styles.modalFooterSplit}>
                            <button type="button" className={styles.primaryBtn} onClick={() => setIsEditing(true)} disabled={loading}>
                                Edit
                            </button>
                            <button type="button" className={styles.dangerTextBtn} onClick={handleDelete} disabled={loading}>
                                Delete external VDR
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className={styles.modalBody}>
                            <label className={styles.fieldLabel}>Link to external VDR</label>
                            <input className={styles.input} value={link} onChange={e => setLink(e.target.value)} placeholder="https://" />
                            <p className={styles.helperText}>
                                Files in an external VDR are not available to Lens analysis unless they are uploaded to Lens.
                            </p>
                            <label className={styles.fieldLabel}>Notes / access instructions</label>
                            <textarea
                                className={styles.textarea}
                                value={notes}
                                onChange={e => setNotes(e.target.value)}
                                placeholder="Do not enter passwords or secrets."
                            />
                        </div>
                        <div className={styles.modalFooter}>
                            <button type="button" className={styles.primaryBtn} onClick={handleSave} disabled={loading}>
                                Save link
                            </button>
                            <button
                                type="button"
                                className={`${styles.secondaryBtn} ${styles.modalCancel}`}
                                onClick={() => (hasExistingVDR ? setIsEditing(false) : handleClose())}>
                                Cancel
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ExternalVDRModal;
